import React, { useState } from 'react';
import { 
  Box, 
  Paper, 
  TextField, 
  Button, 
  Typography, 
  Alert,
  Stack,
  Link,
  IconButton,
  InputAdornment
} from '@mui/material';
import {
  LockReset as LockResetIcon,
  ArrowBack as ArrowBackIcon,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import PageLayout from '../components/PageLayout';

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const tokenFromEmail = searchParams.get('token') || '';
  const resetViaEmailLink = Boolean(tokenFromEmail);
  const emailFromState = location.state?.email || '';

  const [formData, setFormData] = useState({
    email: emailFromState,
    code: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    // Validate password strength
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    if (!resetViaEmailLink && !formData.code.trim()) {
      setError('Verification code is required');
      setLoading(false);
      return;
    }

    try {
      const payload = resetViaEmailLink
        ? { token: tokenFromEmail, password: formData.password }
        : { code: formData.code, password: formData.password };

      const response = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(data.error || 'Failed to reset password');
      }
    } catch (err) {
      setError('Failed to connect to server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageLayout showNavbar={false}>
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        sx={{ py: 4 }}
      >
        <Paper 
          elevation={3} 
          sx={{ 
            p: { xs: 3, sm: 4, md: 5 }, 
            width: '100%', 
            maxWidth: 500,
            border: '1px solid',
            borderColor: 'grey.200'
          }}
        >
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box 
              sx={{ 
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 64,
                height: 64,
                borderRadius: 2,
                backgroundColor: 'primary.main',
                mb: 2
              }}
            >
              <LockResetIcon sx={{ fontSize: 32, color: 'white' }} />
            </Box>
            <Typography variant="h4" component="h1" gutterBottom fontWeight={700}>
              Reset Password
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {resetViaEmailLink
                ? 'Choose a new password for your account (you opened the reset link from your email).'
                : 'Enter the code from your email and choose a new password'}
            </Typography>
          </Box>

          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3,
                borderRadius: 2,
                '& .MuiAlert-message': {
                  fontSize: '0.875rem'
                }
              }}
            >
              {error}
            </Alert>
          )}

          {success && (
            <Alert 
              severity="success" 
              sx={{ 
                mb: 3,
                borderRadius: 2
              }}
            >
              <Typography variant="body2" gutterBottom>
                <strong>Password Reset Successful!</strong>
              </Typography>
              <Typography variant="body2">
                Your password has been changed. Redirecting to login...
              </Typography>
            </Alert>
          )}

          {!success && (
            <form onSubmit={handleSubmit}>
              <Stack spacing={3}>
                {!resetViaEmailLink && (
                  <TextField
                    fullWidth
                    label="Email Address"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    autoComplete="email"
                    variant="outlined"
                    disabled
                    sx={{
                      '& .MuiInputLabel-root': {
                        fontWeight: 500
                      }
                    }}
                  />
                )}

                {!resetViaEmailLink && (
                  <TextField
                    fullWidth
                    label="Verification Code"
                    name="code"
                    type="text"
                    value={formData.code}
                    onChange={handleChange}
                    required
                    autoFocus
                    placeholder="Enter 6-digit code"
                    variant="outlined"
                    inputProps={{
                      maxLength: 6,
                      pattern: '[0-9]{6}',
                      style: { letterSpacing: '0.5em', fontSize: '1.2rem', textAlign: 'center' }
                    }}
                    sx={{
                      '& .MuiInputLabel-root': {
                        fontWeight: 500
                      }
                    }}
                    helperText="Enter the 6-digit code sent to your email"
                  />
                )}
                
                <TextField
                  fullWidth
                  label="New Password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  required
                  autoComplete="new-password"
                  autoFocus={resetViaEmailLink}
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiInputLabel-root': {
                      fontWeight: 500
                    }
                  }}
                  helperText="Must be at least 6 characters"
                />

                <TextField
                  fullWidth
                  label="Confirm New Password"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  autoComplete="new-password"
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                          size="small"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiInputLabel-root': {
                      fontWeight: 500
                    }
                  }}
                />
                
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{ 
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600
                  }}
                >
                  {loading ? 'Resetting Password...' : 'Reset Password'}
                </Button>
              </Stack>
            </form>
          )}

          <Box sx={{ textAlign: 'center', pt: 3, mt: 3, borderTop: '1px solid', borderColor: 'grey.200' }}>
            <Link
              component="button"
              variant="body2"
              onClick={() => navigate('/login')}
              sx={{
                fontWeight: 600,
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 16 }} />
              Back to Login
            </Link>
          </Box>
        </Paper>
      </Box>
    </PageLayout>
  );
};

export default ResetPassword;


